import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraAssistPanel, SpecraButton, SpecraIconButton, SpecraLogo } from '@mono/ui-components';
import { Expand, X } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { useChatScroll } from '../hooks/useChatScroll';
import { useChatHistory } from '../hooks/useConversation';
import { useShanksChatConnect } from '../hooks/useShanksChatConnect';
import { assistConversationId, envelopeChip, expandDestination, parseChatOrigin, resolveAssistPath } from '../lib/chat/envelope';
import { useLayoutStore } from '../lib/stores/LayoutStore';
import { ChatHistoryUnavailable, ChatTurnError } from './ChatView/ChatRecovery';
import { ChatThread } from './ChatView/ChatThread';
import { convertShanksToChatMessages, mergeMessagesById } from './ChatView/shanksMessageAdapter';
import { ComposerInput } from './ComposerInput';
/**
 * The app-wide Assist rail: a shanks chat docked to the right edge of the shell
 * (same primitives as AISidebarShell — ChatThread + ComposerInput). It mounts
 * in the app shell and persists across navigation. Bind changes remount the
 * session so the same program/pursuit resumes the same conversation id.
 * "Open in full view" hands off to the workspace for wherever the chat
 * originated; ⌘J / close hides it.
 */ export function AssistRail({ onClose }) {
    const location = useLocation();
    const assistOrigin = useLayoutStore((s)=>s.assistOrigin);
    const { user } = useAuth();
    const envelope = useMemo(()=>parseChatOrigin(resolveAssistPath(location.pathname, assistOrigin)), [
        location.pathname,
        assistOrigin
    ]);
    const chatId = assistConversationId(envelope, user?.id);
    return /*#__PURE__*/ _jsx(AssistRailSession, {
        chatId: chatId,
        envelope: envelope,
        originPath: resolveAssistPath(location.pathname, assistOrigin),
        onClose: onClose
    }, chatId);
}
function AssistRailSession({ chatId, envelope, originPath, onClose }) {
    const navigate = useNavigate();
    const history = useChatHistory(chatId, sessionStorage.getItem(`assist-started::${chatId}`) !== 'true');
    const { scrollRef, awayFromBottom, jumpToLatest } = useChatScroll(chatId);
    const rememberConversation = useCallback(()=>sessionStorage.setItem(`assist-started::${chatId}`, 'true'), [
        chatId
    ]);
    const savedSubmission = history.data?.submissions?.find((s)=>s.state === 'running') ?? history.data?.submissions?.[0];
    const restoredSubmission = useMemo(()=>savedSubmission?.message ? {
            id: savedSubmission.submissionId,
            content: savedSubmission.message,
            fileIds: savedSubmission.fileIds,
            state: savedSubmission.state
        } : undefined, [
        savedSubmission
    ]);
    const shanks = useShanksChatConnect({
        chatId,
        initialConversationId: chatId,
        projectId: envelope.projectId,
        context: envelope,
        onConversationId: rememberConversation,
        onFinish: ()=>void history.refetch(),
        onError: ()=>void history.refetch(),
        restoredSubmission
    });
    const responseBusy = shanks.isLoading || !history.data?.readOnly && savedSubmission?.state === 'running';
    const unfinished = !history.data?.readOnly && savedSubmission && [
        'incomplete',
        'failed',
        'interrupted'
    ].includes(savedSubmission.state);
    const incomplete = shanks.incomplete || !shanks.error && unfinished && savedSubmission.state === 'incomplete';
    const turnError = responseBusy ? null : shanks.error ?? (unfinished ? incomplete ? 'This request needs more work. Your progress is saved. Continue to finish it.' : 'This response was interrupted. Retry your saved request.' : null);
    const messages = useMemo(()=>mergeMessagesById(history.data?.messages ?? [], convertShanksToChatMessages(shanks.messages), shanks.isLoading), [
        history.data?.messages,
        shanks.messages,
        shanks.isLoading
    ]);
    const chip = envelopeChip(envelope);
    // Accept/Reject/Comment on a card in the rail continues this conversation.
    // The rail never wired the old global feedback bus, so its card follow-ups
    // were dropped (or captured by an unrelated chat surface); scope it here.
    const sendCardFeedback = useCallback((message)=>{
        if (!responseBusy && !history.isLoading && !history.isError) void shanks.sendMessage(message);
    }, [
        shanks,
        responseBusy,
        history.isError,
        history.isLoading
    ]);
    const handleSubmit = async (data)=>{
        const content = data.prompt.trim();
        if (!content || responseBusy || history.isLoading || history.isError) return;
        const fileIds = data.attachments?.map((a)=>a.id).filter((id)=>!!id);
        await shanks.sendMessage(content, {
            fileIds
        });
    };
    const suggestions = [
        'What deliverables are overdue?',
        'Summarize the SDS audit',
        'Which CLINs are at risk?'
    ];
    return /*#__PURE__*/ _jsxs(SpecraAssistPanel, {
        transcriptRef: scrollRef,
        composer: history.isLoading || history.isError ? undefined : /*#__PURE__*/ _jsx(ComposerInput, {
            dense: true,
            mode: "chat",
            onSubmit: handleSubmit,
            placeholder: "Ask Specra…",
            submitLabel: "Send",
            submitting: responseBusy,
            onStop: shanks.stop
        }),
        headerActions: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SpecraIconButton, {
                    label: "Open in full view",
                    onClick: ()=>{
                        const dest = expandDestination(originPath, shanks.conversationId ?? chatId);
                        if (dest === '/assistant' && envelope.surface !== 'root' && envelope.surface !== 'assistant') {
                            navigate('/assistant', {
                                state: {
                                    seedPins: [
                                        envelope
                                    ]
                                }
                            });
                            return;
                        }
                        navigate(dest);
                    },
                    size: "sm",
                    variant: "ghost",
                    children: /*#__PURE__*/ _jsx(Expand, {
                        "aria-hidden": "true"
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraIconButton, {
                    label: "Close (⌘J)",
                    onClick: onClose,
                    size: "sm",
                    variant: "ghost",
                    children: /*#__PURE__*/ _jsx(X, {
                        "aria-hidden": "true"
                    })
                })
            ]
        }),
        title: chip ? /*#__PURE__*/ _jsxs("span", {
            className: "flex min-w-0 items-center gap-2",
            children: [
                /*#__PURE__*/ _jsx("span", {
                    children: "Assist"
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "truncate rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-normal text-muted-foreground",
                    children: chip
                })
            ]
        }) : 'Assist',
        children: [
            history.isLoading ? /*#__PURE__*/ _jsx("output", {
                children: "Loading conversation…"
            }) : null,
            history.isError ? /*#__PURE__*/ _jsx(ChatHistoryUnavailable, {
                error: history.error,
                onRetry: ()=>void history.refetch()
            }) : null,
            history.isLoading || history.isError ? null : messages.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-[9px]",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-start gap-[9px]",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "mt-px flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-[6px] bg-muted",
                                children: /*#__PURE__*/ _jsx(SpecraLogo, {
                                    variant: "mark",
                                    size: 12,
                                    label: ""
                                })
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "min-w-0 flex-1 pt-0.5 text-[12.5px] leading-[1.6] text-foreground",
                                children: "Ask about anything on your screen — programs, deliverables, documents, or the work ahead."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex flex-wrap gap-[6px] pl-[31px]",
                        children: suggestions.map((q)=>/*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: ()=>void shanks.sendMessage(q),
                                disabled: responseBusy || history.isLoading || history.isError,
                                className: "inline-flex items-center rounded-full border border-border bg-card px-[11px] py-[5px] text-[11.5px] text-foreground transition-colors hover:border-muted-foreground disabled:opacity-60",
                                children: q
                            }, q))
                    })
                ]
            }) : /*#__PURE__*/ _jsx(ChatThread, {
                chatId: chatId,
                isSending: responseBusy,
                messages: messages,
                variant: "assist",
                onFeedback: sendCardFeedback
            }),
            turnError ? /*#__PURE__*/ _jsx(ChatTurnError, {
                error: turnError,
                incomplete: incomplete,
                onRetry: ()=>void shanks.retry()
            }) : null,
            awayFromBottom ? /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                size: "sm",
                className: "sticky bottom-3 z-10",
                onClick: jumpToLatest,
                children: "Jump to latest"
            }) : null
        ]
    });
}
